/* eslint-disable import/order */
import 'normalize.css';
import {
    initializeDeviceUiInfoTracking,
    SbNumberFormatterPlugin,
    DateTimeFormatterPlugin,
    SbDisplayPlugin,
} from '@superbet-group/web.lib.vue-utilities';
import VueCompositionApi from '@vue/composition-api';
import PortalVue from 'portal-vue';
import Vue from 'vue';
import * as VueScrollTo from 'vue-scrollto';
import VueWaypoint from 'vue-waypoint';
import { sync } from 'vuex-router-sync';
// import * as localForage from 'localforage';

import '@src/config/boot';

// Maintenance service should be imported early for faster launch darkly initialization.
import { featureFlagService } from '@superbet-group/web.lib.flags';

import '@app/styles/main.scss';
import i18n from '@app/localization/i18n';
import { initI18n } from '@src/app/localization/i18n/getI18n';
import * as filters from '@shared/filters';

import modals from '@store/modules/ui/shared/modal/availableModals';
import { registerModals } from '@store/modules/ui/shared/modal/modalsRegistry';
import { initErrorGuard } from '@utils/ErrorGuard';
import { createErrorGuardConfig } from '@utils/ErrorGuard/storeConfig';

import App from './App.vue';

import router from '@app/router';

import store, { initializeSubscriptions } from './app/store';
import { initializeWebpCheck } from './app/styles/imageHelpers';
import { initializeMobileHelpers } from './app/styles/mobileHelpers';

import SuperAnalytics from '@superbet-group/vue-analytics-plugin';
import { initializeNetworkAnalytics } from '@app/utils/networkAnalytics';
import { getConfig } from '@superbet-group/web.lib.config';
import { getShellConfig } from '@src/config/shell/config';
import { WikiPath } from '@modules/wiki/WikiPath';
import {
    AnalyticsLifecycleManager,
    Analytics as AnalyticsSDK,
} from '@superbet-group/web.analytics.sdk';
import { getDeviceId } from './config/idGenerationService';
import { antiFraudService } from '@core/services/antiFraud/AntiFraudService';
import { NotificationVuePlugin } from '@modules/notifications/plugin';
import { wafService } from '@core/services/waf/WafService';
import modalCodes from '@store/modules/ui/shared/modal/modalCodes';
import { capitalize } from '@superbet-group/web.lib.utilities';
import { ObservabilitySDKConfig } from '@superbet-group/web.observability.sdk-bootstrap';
import {
    getBootingTheme,
    setHtmlTheme,
} from './app/shared/theme/theme';
import { MeticulousTargetSupportUtil } from './app/utils/meticulous/meticulousUtils';
import {
    isOnSetFeaturePath,
    setCookies,
} from './app/modals/modals/featureTest/domain';

initI18n(i18n);

antiFraudService.setEnabled(true);

const config = getConfig();
wafService.initialize(config);
registerModals(modals);

Vue.config.devtools = process.env.NODE_ENV === 'development';

if (!config.environment.isTest) {
    (window as any).toEvent = {};
    (window as any).denormalizeEvent = {};
    (window as any).containsFullOddList = {};
    (window as any).getAllChanges = {};
    (window as any).removeEventsInMaintenance = {};
    (window as any).in = {};
    (window as any).subscribeToEventChanges = {};
    (window as any).setEvents = {};
    (window as any).setEventsMutationsInStore = {};
    (window as any).setEventsMutationsNotInStore = {};
}

if (!config.environment.isProdEnvironment || window.location.hostname.includes('stage.on')) {
    window.addEventListener('keydown', (event: KeyboardEvent) => {
        // For translation dev/test purposes - in non-production Ctrl + K
        // will flash original translation keys for 5 seconds. K stands for keys,
        // and T is taken for tabs :)
        if (event.ctrlKey && event.key.toLocaleLowerCase() === 'k') {
            flashTranslationKeys();
        }

        if (event.key === '/' && event.ctrlKey) {
            store.dispatch(
                'ui/modal/setModal',
                modalCodes.setFeatureBranch,
                { root: true },
            );
        }
    }, { passive: true });

    // On landing, see if we need to set up feature branches.
    // Dev-only feature, so do not connect to the router, do on bootstrap
    const queryParams = new URLSearchParams(window.location.search);

    if (isOnSetFeaturePath()) {
        const branches = Object.fromEntries(queryParams.entries());
        setCookies(branches);
    }
}

// Meticulous init when recording
MeticulousTargetSupportUtil.recordTargetCountry(config.countrySpecific.country);

Vue.config.productionTip = false;
Object.keys(filters).forEach((key) => {
    Vue.filter(key, (filters as Record<string, any>)[key]);
});

sync(store, router);

Vue.prototype.$WikiPath = WikiPath;
Vue.prototype.$capitalize = capitalize;
Vue.use(VueCompositionApi);
Vue.use(VueWaypoint);
Vue.use(VueScrollTo as any);
Vue.use(PortalVue);
Vue.use(NotificationVuePlugin);
Vue.use(SbNumberFormatterPlugin, {
    currencyLocale: config.appLocale.locale,
    numberLocale: config.appLocale.localeNumberOverride,
    // TODO(@the-overengineer): Configure for all environments (PL is special and should use ' ').
    // Defaults to '' --> oddValueThousandsSeparator: '',
    i18n,
});
Vue.use(DateTimeFormatterPlugin, {
    appLocale: config.appLocale.locale,
    i18n,
});
// Exposes the $display helpers in the template
Vue.use(SbDisplayPlugin);

const errorGuardConfig = createErrorGuardConfig(store);
initErrorGuard(errorGuardConfig);

Vue.config.ignoredElements = ['lottie-player'];

const superAnalyticsConfig = {
    debug: !config.environment.isProdEnvironment,
    user: getUser,
    getLaunchDarklyAnalyticsFlag,
    appInfo: {
        country: config.countrySpecific.country,
        language: config.appLocale.languageCode,
        version: config.environment.version,
    },
    deviceId: getDeviceId(),
};

Vue.use(SuperAnalytics, superAnalyticsConfig);
AnalyticsSDK.getInstance().setConfig({
    // this cast below prevents an error in the build process.
    // the analytics SDK is strongly typed and config.analytics it not.
    ...config.analytics as any ?? {},
    appInfo: {
        country: config.countrySpecific.country,
        language: config.appLocale.languageCode,
        version: config.environment.version,
    },
});
AnalyticsLifecycleManager
    .getInstance()
    .setUser(getUser)
    .sessionStart();

/**
 * Bootstrap ObservabilitySDK
 * Load multiple URLs
 */
ObservabilitySDKConfig.loadFromUrls(getShellConfig().remoteConfig.observabilitySdk.configs);

// Best guess on initial boot, either use the last visited theme we're aware of, or the system one
if (!getConfig().environment.isProdEnvironment || process.env.NODE_ENV === 'development') {
    const theme = getBootingTheme(store.getters['navigation/currentNavigationContext']);
    setHtmlTheme(theme);
}

new Vue({
    i18n,
    store,
    router,
    el: '#app',
    template: '<App/>',
    components: { App: (App as any) }, // not to have the ts "no overload matches this call"
});

initializeSubscriptions();
initializeDeviceUiInfoTracking();
initializeMobileHelpers();
initializeWebpCheck();
initializeNetworkAnalytics();

function getUser() {
    return store.getters['data/authentication/user'];
}

function getLaunchDarklyAnalyticsFlag() {
    return featureFlagService.getFeatureValue('super-analytics.enabled', true);
}

function flashTranslationKeys() {
    const currentLocale = i18n.locale;
    i18n.locale = ''; // With this locale, original messages are shown.
    setTimeout(() => {
        i18n.locale = currentLocale;
    }, 5_000);
}
